import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { addNotification } from '../../store/slices/uiSlice';
import posAPI from '../../services/posAPI';
import memberService from '../../services/memberService';
import actionBtn from '../common/actionButtonStyles';

const HKT = 'Asia/Hong_Kong';
const fmtDT = (iso) =>
  iso ? new Date(iso).toLocaleString('zh-TW', { timeZone: HKT, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }) : '';

const hktWeekRange = () => {
  const hktMs = Date.now() + 8 * 3600 * 1000;
  const day = new Date(hktMs).getUTCDay();
  const diff = day === 0 ? -6 : 1 - day;
  const monMs = hktMs + diff * 86400000;
  return {
    from: new Date(monMs).toISOString().slice(0, 10),
    to: new Date(monMs + 6 * 86400000).toISOString().slice(0, 10),
  };
};

const CATEGORIES = [
  { value: '', label: '全部' },
  { value: 'gift', label: '禮品' },
  { value: 'book', label: '書籍' },
  { value: 'course', label: '課程' },
  { value: 'other', label: '其他' },
];
const CAT_LABEL = { gift: '禮品', book: '書籍', course: '課程', other: '其他' };
const STATUS_COLOR = {
  paid: 'bg-green-100 text-green-700',
  unpaid: 'bg-yellow-100 text-yellow-700',
  cancelled: 'bg-red-100 text-red-600',
};

// ── Product Modal ─────────────────────────────────────────────────────────────
const ProductModal = ({ product, onClose, onSaved }) => {
  const dispatch = useDispatch();
  const isEdit = !!product?.id;
  const [form, setForm] = useState({
    name: product?.name ?? '',
    name_tc: product?.name_tc ?? '',
    sku: product?.sku ?? '',
    category: product?.category ?? 'other',
    price: product?.price ?? '',
    stock_qty: product?.stock_qty ?? '',
    description: product?.description ?? '',
    is_active: product?.is_active ?? true,
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name || form.price === '') { dispatch(addNotification({ type: 'error', message: '請填寫產品名稱及價格' })); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        sku: form.sku.trim() || null,
        price: parseFloat(form.price),
        stock_qty: form.stock_qty !== '' ? parseInt(form.stock_qty, 10) : null,
      };
      if (isEdit) await posAPI.updateProduct(product.id, payload);
      else await posAPI.createProduct(payload);
      dispatch(addNotification({ type: 'success', message: isEdit ? '產品已更新' : '產品已新增' }));
      onSaved();
    } catch (e) {
      dispatch(addNotification({ type: 'error', message: e.response?.data?.detail || '儲存失敗' }));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4">
        <h3 className="text-lg font-bold text-gray-900">{isEdit ? '編輯產品' : '新增產品'}</h3>

        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="text-sm font-medium text-gray-700">英文名稱 *</label>
            <input value={form.name} onChange={(e) => set('name', e.target.value)}
              className="mt-1 w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          </div>
          <div className="col-span-2">
            <label className="text-sm font-medium text-gray-700">中文名稱</label>
            <input value={form.name_tc} onChange={(e) => set('name_tc', e.target.value)}
              className="mt-1 w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          </div>
          <div className="col-span-2">
            <label className="text-sm font-medium text-gray-700">SKU / 條碼 / QR Code</label>
            <input value={form.sku} onChange={(e) => set('sku', e.target.value)}
              placeholder="可用掃描器掃入，或手動輸入"
              className="mt-1 w-full border rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">類別</label>
            <select value={form.category} onChange={(e) => set('category', e.target.value)}
              className="mt-1 w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
              {CATEGORIES.slice(1).map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">價格 (HKD) *</label>
            <input type="number" min="0" step="0.01" value={form.price} onChange={(e) => set('price', e.target.value)}
              className="mt-1 w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">庫存數量（空白=無限）</label>
            <input type="number" min="0" value={form.stock_qty} onChange={(e) => set('stock_qty', e.target.value)}
              className="mt-1 w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          </div>
          <div className="flex items-center gap-2 pt-5">
            <input type="checkbox" id="is_active" checked={form.is_active} onChange={(e) => set('is_active', e.target.checked)}
              className="w-4 h-4 text-indigo-600 rounded" />
            <label htmlFor="is_active" className="text-sm text-gray-700">啟用</label>
          </div>
          <div className="col-span-2">
            <label className="text-sm font-medium text-gray-700">描述</label>
            <textarea rows={2} value={form.description} onChange={(e) => set('description', e.target.value)}
              className="mt-1 w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">取消</button>
          <button onClick={handleSave} disabled={saving}
            className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">
            {saving ? '儲存中…' : '儲存'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Checkout Modal ────────────────────────────────────────────────────────────
const CheckoutModal = ({ cart, onClose, onConfirm }) => {
  const dispatch = useDispatch();
  const [worshipNo, setWorshipNo] = useState('');
  const [memberSearching, setMemberSearching] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [cellGroup, setCellGroup] = useState('');
  const [memberId, setMemberId] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState('paid');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const total = cart.reduce((s, i) => s + i.price * i.qty, 0);

  const handleMemberSearch = async () => {
    if (!worshipNo.trim()) return;
    setMemberSearching(true);
    try {
      const res = await memberService.searchByWorshipNo(worshipNo.trim());
      const m = res.data;
      setCustomerName(m.chi_name || '');
      setCellGroup(m.groups || m.cell_group_name4 || '');
      setMemberId(m.id);
    } catch {
      dispatch(addNotification({ type: 'error', message: `找不到崇拜編號 ${worshipNo} 的會員` }));
    } finally { setMemberSearching(false); }
  };

  const handleConfirm = async () => {
    setSaving(true);
    try {
      await onConfirm({
        customer_name: customerName.trim() || '-',
        customer_cell_group: cellGroup || null,
        member_id: memberId || null,
        payment_status: paymentStatus,
        notes: notes || null,
        items: cart.map((i) => ({
          product_id: i.id,
          product_name: i.name_tc || i.name,
          unit_price: i.price,
          quantity: i.qty,
          subtotal: parseFloat((i.price * i.qty).toFixed(2)),
        })),
      });
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-bold text-gray-900">結帳</h3>

        <div className="border rounded-lg divide-y text-sm">
          {cart.map((i) => (
            <div key={i.id} className="flex justify-between px-3 py-2">
              <span>{i.name_tc || i.name} × {i.qty}</span>
              <span>HK${(i.price * i.qty).toFixed(2)}</span>
            </div>
          ))}
          <div className="flex justify-between px-3 py-2 font-bold text-base">
            <span>合計</span>
            <span className="text-indigo-700">HK${total.toFixed(2)}</span>
          </div>
        </div>

        {/* Member search by worship number */}
        <div>
          <label className="text-sm font-medium text-gray-700">崇拜編號搜尋</label>
          <div className="mt-1 flex gap-2">
            <input
              value={worshipNo}
              onChange={(e) => setWorshipNo(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleMemberSearch()}
              placeholder="輸入崇拜編號"
              className="flex-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <button
              onClick={handleMemberSearch}
              disabled={memberSearching || !worshipNo.trim()}
              className="px-3 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 disabled:opacity-50"
            >
              {memberSearching ? '…' : '搜尋'}
            </button>
          </div>
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700">顧客姓名 *</label>
          <input value={customerName} onChange={(e) => setCustomerName(e.target.value)}
            placeholder="請輸入姓名（或先搜尋崇拜編號）"
            className="mt-1 w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700">小組 / 區域（選填）</label>
          <input value={cellGroup} onChange={(e) => setCellGroup(e.target.value)}
            placeholder="搜尋會員後自動填入，或手動輸入"
            className="mt-1 w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700">付款狀態</label>
          <select value={paymentStatus} onChange={(e) => setPaymentStatus(e.target.value)}
            className="mt-1 w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
            <option value="paid">已付款</option>
            <option value="unpaid">未付款（記錄欠款）</option>
          </select>
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700">備註（選填）</label>
          <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
            className="mt-1 w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">取消</button>
          <button onClick={handleConfirm} disabled={saving}
            className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">
            {saving ? '處理中…' : '確認訂單'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
const POSPage = () => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState('pos'); // pos | products | orders
  const skuInputRef = useRef(null);
  const [skuValue, setSkuValue] = useState('');

  // Products
  const [products, setProducts] = useState([]);
  const [catFilter, setCatFilter] = useState('');
  const [productModal, setProductModal] = useState(null); // null | {} | product

  // Cart
  const [cart, setCart] = useState([]);
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  // Orders
  const [orders, setOrders] = useState([]);
  const [orderTotal, setOrderTotal] = useState(0);
  const [orderLoading, setOrderLoading] = useState(false);
  const week = hktWeekRange();
  const [orderDateFrom, setOrderDateFrom] = useState(week.from);
  const [orderDateTo, setOrderDateTo] = useState(week.to);
  const [orderStatusFilter, setOrderStatusFilter] = useState('');

  // ── Loaders ──
  const loadProducts = useCallback(async () => {
    try {
      const res = await posAPI.listProducts({ include_inactive: true });
      setProducts(res.data);
    } catch {
      dispatch(addNotification({ type: 'error', message: '無法載入產品' }));
    }
  }, [dispatch]);

  const loadOrders = useCallback(async () => {
    setOrderLoading(true);
    try {
      const params = {};
      if (orderDateFrom) params.date_from = orderDateFrom;
      if (orderDateTo) params.date_to = orderDateTo;
      if (orderStatusFilter) params.payment_status = orderStatusFilter;
      const res = await posAPI.listOrders(params);
      setOrders(res.data.items);
      setOrderTotal(res.data.total);
    } catch {
      dispatch(addNotification({ type: 'error', message: '無法載入訂單' }));
    } finally { setOrderLoading(false); }
  }, [dispatch, orderDateFrom, orderDateTo, orderStatusFilter]);

  useEffect(() => { loadProducts(); }, [loadProducts]);
  useEffect(() => { if (activeTab === 'orders') loadOrders(); }, [activeTab, loadOrders]);
  useEffect(() => { if (activeTab === 'pos') setTimeout(() => skuInputRef.current?.focus(), 50); }, [activeTab]);

  // ── SKU scan handler ──
  const handleSkuScan = async (e) => {
    if (e.key !== 'Enter') return;
    const sku = skuValue.trim();
    if (!sku) return;
    try {
      const res = await posAPI.getProductBySku(sku);
      addToCart(res.data);
      setSkuValue('');
    } catch {
      dispatch(addNotification({ type: 'error', message: `找不到 SKU: ${sku}` }));
      setSkuValue('');
    }
    skuInputRef.current?.focus();
  };

  // ── Cart helpers ──
  const addToCart = (product) => {
    if (product.stock_qty !== null && product.stock_qty <= 0) {
      dispatch(addNotification({ type: 'error', message: `「${product.name_tc || product.name}」庫存不足` }));
      return;
    }
    setCart((prev) => {
      const idx = prev.findIndex((i) => i.id === product.id);
      if (idx >= 0) {
        const updated = [...prev];
        updated[idx] = { ...updated[idx], qty: updated[idx].qty + 1 };
        return updated;
      }
      return [...prev, { id: product.id, name: product.name, name_tc: product.name_tc, price: parseFloat(product.price), qty: 1 }];
    });
  };

  const setQty = (id, qty) => {
    if (qty <= 0) { setCart((prev) => prev.filter((i) => i.id !== id)); return; }
    setCart((prev) => prev.map((i) => i.id === id ? { ...i, qty } : i));
  };

  const cartTotal = cart.reduce((s, i) => s + i.price * i.qty, 0);

  // ── Print receipt ──
  const printReceipt = (order) => {
    const hktStr = (iso) => iso ? new Date(iso).toLocaleString('zh-TW', {
      timeZone: HKT, year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false,
    }) : '';
    const statusLabel = { paid: '已付款', unpaid: '未付款', cancelled: '已取消' };
    const el = document.createElement('div');
    el.id = '__pos_receipt__';
    el.innerHTML = `
      <style>
        @media print {
          body > *:not(#__pos_receipt__) { display: none !important; }
          #__pos_receipt__ { display: block !important; font-family: monospace; font-size: 12px; width: 80mm; margin: 0 auto; color: #000; }
          #__pos_receipt__ h1 { font-size: 16px; text-align: center; margin: 4px 0; }
          #__pos_receipt__ .c { text-align: center; margin: 2px 0; }
          #__pos_receipt__ .hr { border: none; border-top: 1px dashed #000; margin: 6px 0; }
          #__pos_receipt__ table { width: 100%; border-collapse: collapse; }
          #__pos_receipt__ td { padding: 2px 0; vertical-align: top; }
          #__pos_receipt__ .r { text-align: right; }
          #__pos_receipt__ .bold { font-weight: bold; font-size: 14px; }
        }
      </style>
      <h1>Crossroad Community Baptist Church</h1>
      <p class="c">阡陌社區浸信會</p>
      <div class="hr"></div>
      <p>訂單: ${order.order_no}</p>
      <p>日期: ${hktStr(order.created_at)}</p>
      <p>顧客: ${order.customer_name}</p>
      ${order.customer_cell_group ? `<p>小組: ${order.customer_cell_group}</p>` : ''}
      <div class="hr"></div>
      <table>
        <tr><td><b>產品</b></td><td class="r"><b>數量</b></td><td class="r"><b>金額</b></td></tr>
        ${(order.items || []).map((i) => `<tr><td>${i.product_name}</td><td class="r">×${i.quantity}</td><td class="r">HK$${parseFloat(i.subtotal).toFixed(2)}</td></tr>`).join('')}
      </table>
      <div class="hr"></div>
      <p class="r bold">合計: HK$${parseFloat(order.total_amount).toFixed(2)}</p>
      <p class="r">付款狀態: ${statusLabel[order.payment_status] || order.payment_status}</p>
      <div class="hr"></div>
      <p class="c">謝謝惠顧！God Bless You!</p>
    `;
    document.body.appendChild(el);
    window.print();
    document.body.removeChild(el);
  };

  // ── Checkout ──
  const handleCheckout = async (data) => {
    try {
      const res = await posAPI.createOrder(data);
      dispatch(addNotification({ type: 'success', message: `訂單已建立` }));
      setCart([]);
      setCheckoutOpen(false);
      loadProducts(); // refresh stock
      printReceipt(res.data);
    } catch (e) {
      dispatch(addNotification({ type: 'error', message: e.response?.data?.detail || '訂單建立失敗' }));
      throw e;
    }
  };

  // ── Product delete ──
  const handleDeleteProduct = async (product) => {
    if (!window.confirm(`確定刪除「${product.name_tc || product.name}」？`)) return;
    try {
      await posAPI.deleteProduct(product.id);
      dispatch(addNotification({ type: 'success', message: '已刪除' }));
      loadProducts();
    } catch {
      dispatch(addNotification({ type: 'error', message: '刪除失敗' }));
    }
  };

  // ── Order status update ──
  const handleOrderStatus = async (id, newStatus) => {
    try {
      await posAPI.updateOrderStatus(id, newStatus);
      dispatch(addNotification({ type: 'success', message: '狀態已更新' }));
      loadOrders();
    } catch {
      dispatch(addNotification({ type: 'error', message: '更新失敗' }));
    }
  };

  // ── Order delete ──
  const handleDeleteOrder = async (id) => {
    if (!window.confirm('確定刪除此訂單？')) return;
    try {
      await posAPI.deleteOrder(id);
      dispatch(addNotification({ type: 'success', message: '訂單已刪除' }));
      loadOrders();
    } catch {
      dispatch(addNotification({ type: 'error', message: '刪除失敗' }));
    }
  };

  const activeProducts = products.filter((p) => p.is_active && (!catFilter || p.category === catFilter));

  const TABS = [
    { id: 'pos', label: '🛒 POS 收銀' },
    { id: 'products', label: '📦 產品管理' },
    { id: 'orders', label: '📋 訂單記錄' },
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Tab Bar */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === t.id ? 'bg-white shadow text-indigo-700' : 'text-gray-500 hover:text-gray-700'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── POS Terminal ── */}
      {activeTab === 'pos' && (
        <div className="flex gap-6 h-[calc(100vh-220px)] min-h-[500px]">
          {/* Product Grid */}
          <div className="flex-1 flex flex-col gap-4 overflow-hidden">
            {/* SKU / Barcode scanner bar */}
            <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2">
              <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M4 6h1M4 10h1M4 14h1M4 18h1M8 6v12M11 6h1M11 10h1M11 14h1M11 18h1M15 6v12M18 6h1M18 10h1M18 14h1M18 18h1" />
              </svg>
              <input
                ref={skuInputRef}
                value={skuValue}
                onChange={(e) => setSkuValue(e.target.value)}
                onKeyDown={handleSkuScan}
                placeholder="掃描條碼或輸入 SKU，按 Enter 加入購物車"
                className="flex-1 bg-transparent text-sm font-mono focus:outline-none placeholder-gray-400"
              />
              {skuValue && (
                <button onClick={() => { setSkuValue(''); skuInputRef.current?.focus(); }}
                  className="text-gray-400 hover:text-gray-600 text-xs">✕</button>
              )}
            </div>

            {/* Category filter */}
            <div className="flex gap-2 flex-wrap">
              {CATEGORIES.map((c) => (
                <button key={c.value} onClick={() => setCatFilter(c.value)}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${catFilter === c.value ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-300 hover:border-indigo-400'}`}>
                  {c.label}
                </button>
              ))}
            </div>

            {/* Products */}
            <div className="flex-1 overflow-y-auto">
              {activeProducts.length === 0 ? (
                <p className="text-sm text-gray-400 py-10 text-center">沒有產品，請先在「產品管理」新增</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
                  {activeProducts.map((p) => {
                    const outOfStock = p.stock_qty !== null && p.stock_qty <= 0;
                    return (
                      <button key={p.id} onClick={() => addToCart(p)} disabled={outOfStock}
                        className={`bg-white rounded-xl border p-4 text-left hover:border-indigo-400 hover:shadow-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed`}>
                        <div className="text-xs text-gray-400 mb-1">{CAT_LABEL[p.category] || p.category}</div>
                        <div className="font-semibold text-gray-900 text-sm leading-tight">{p.name_tc || p.name}</div>
                        {p.name_tc && <div className="text-xs text-gray-500">{p.name}</div>}
                        <div className="mt-2 text-indigo-700 font-bold">HK${parseFloat(p.price).toFixed(2)}</div>
                        {p.stock_qty !== null && (
                          <div className={`text-xs mt-1 ${outOfStock ? 'text-red-500' : 'text-gray-400'}`}>
                            庫存: {p.stock_qty}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Cart */}
          <div className="w-80 flex flex-col bg-white rounded-2xl border shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b font-semibold text-gray-900">購物車</div>

            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
              {cart.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-8">尚未加入任何產品</p>
              ) : cart.map((item) => (
                <div key={item.id} className="flex items-center gap-3 text-sm">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 truncate">{item.name_tc || item.name}</div>
                    <div className="text-gray-500">HK${item.price.toFixed(2)} / 件</div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button onClick={() => setQty(item.id, item.qty - 1)}
                      className="w-6 h-6 rounded-full border text-gray-600 hover:bg-gray-100 text-base leading-none">−</button>
                    <span className="w-5 text-center font-medium">{item.qty}</span>
                    <button onClick={() => setQty(item.id, item.qty + 1)}
                      className="w-6 h-6 rounded-full border text-gray-600 hover:bg-gray-100 text-base leading-none">+</button>
                  </div>
                  <div className="w-16 text-right font-medium text-gray-900">
                    HK${(item.price * item.qty).toFixed(2)}
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t px-5 py-4 space-y-3">
              <div className="flex justify-between font-bold text-base">
                <span>合計</span>
                <span className="text-indigo-700">HK${cartTotal.toFixed(2)}</span>
              </div>
              <button onClick={() => setCheckoutOpen(true)} disabled={cart.length === 0}
                className="w-full bg-green-600 text-white py-3 rounded-xl font-semibold hover:bg-green-700 disabled:opacity-40 transition-colors">
                結帳
              </button>
              {cart.length > 0 && (
                <button onClick={() => setCart([])}
                  className="w-full text-sm text-gray-400 hover:text-red-500 transition-colors">
                  清空購物車
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Products Management ── */}
      {activeTab === 'products' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-500">共 {products.length} 項產品</span>
            <button onClick={() => setProductModal({})}
              className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              新增產品
            </button>
          </div>

          <div className="bg-white rounded-2xl border shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                <tr>
                  <th className="px-4 py-3 text-left">產品名稱</th>
                  <th className="px-4 py-3 text-left">類別</th>
                  <th className="px-4 py-3 text-right">價格</th>
                  <th className="px-4 py-3 text-right">庫存</th>
                  <th className="px-4 py-3 text-center">狀態</th>
                  <th className="px-4 py-3 text-right">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {products.length === 0 && (
                  <tr><td colSpan={6} className="text-center py-10 text-gray-400">未有產品</td></tr>
                )}
                {products.map((p) => (
                  <tr key={p.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{p.name_tc || p.name}</div>
                      {p.name_tc && <div className="text-xs text-gray-400">{p.name}</div>}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{CAT_LABEL[p.category] || p.category}</td>
                    <td className="px-4 py-3 text-right font-medium text-gray-900">HK${parseFloat(p.price).toFixed(2)}</td>
                    <td className="px-4 py-3 text-right text-gray-600">{p.stock_qty ?? '∞'}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${p.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {p.is_active ? '啟用' : '停用'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right space-x-2">
                      <button onClick={() => setProductModal(p)}
                        className={actionBtn.edit}>編輯</button>
                      <button onClick={() => handleDeleteProduct(p)}
                        className={actionBtn.delete}>刪除</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Orders ── */}
      {activeTab === 'orders' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <label className="text-xs text-gray-500 block mb-1">由</label>
              <input type="date" value={orderDateFrom} onChange={(e) => setOrderDateFrom(e.target.value)}
                className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">至</label>
              <input type="date" value={orderDateTo} onChange={(e) => setOrderDateTo(e.target.value)}
                className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">付款狀態</label>
              <select value={orderStatusFilter} onChange={(e) => setOrderStatusFilter(e.target.value)}
                className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                <option value="">全部</option>
                <option value="paid">已付款</option>
                <option value="unpaid">未付款</option>
                <option value="cancelled">已取消</option>
              </select>
            </div>
            <button onClick={loadOrders}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700">
              搜尋
            </button>
            <span className="text-sm text-gray-500 self-center">共 {orderTotal} 筆</span>
          </div>

          <div className="bg-white rounded-2xl border shadow-sm overflow-hidden">
            {orderLoading ? (
              <p className="text-center py-10 text-gray-400 text-sm">載入中…</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                  <tr>
                    <th className="px-4 py-3 text-left">訂單編號</th>
                    <th className="px-4 py-3 text-left">顧客</th>
                    <th className="px-4 py-3 text-left">產品</th>
                    <th className="px-4 py-3 text-right">金額</th>
                    <th className="px-4 py-3 text-center">付款</th>
                    <th className="px-4 py-3 text-left">日期</th>
                    <th className="px-4 py-3 text-right">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {orders.length === 0 && (
                    <tr><td colSpan={7} className="text-center py-10 text-gray-400">沒有訂單記錄</td></tr>
                  )}
                  {orders.map((o) => (
                    <tr key={o.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono text-indigo-700 text-xs font-medium">{o.order_no}</td>
                      <td className="px-4 py-3 font-medium text-gray-900">{o.customer_name}</td>
                      <td className="px-4 py-3 text-gray-600 text-xs">
                        {o.items.map((i) => `${i.product_name}×${i.quantity}`).join(', ')}
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-gray-900">HK${parseFloat(o.total_amount).toFixed(2)}</td>
                      <td className="px-4 py-3 text-center">
                        <select
                          value={o.payment_status}
                          onChange={(e) => handleOrderStatus(o.id, e.target.value)}
                          className={`text-xs px-2 py-1 rounded-full border-0 font-medium cursor-pointer ${STATUS_COLOR[o.payment_status] || 'bg-gray-100 text-gray-600'}`}
                        >
                          <option value="paid">已付款</option>
                          <option value="unpaid">未付款</option>
                          <option value="cancelled">已取消</option>
                        </select>
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{fmtDT(o.created_at)}</td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => handleDeleteOrder(o.id)}
                          className={actionBtn.delete}>刪除</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Modals */}
      {productModal !== null && (
        <ProductModal
          product={productModal?.id ? productModal : null}
          onClose={() => setProductModal(null)}
          onSaved={() => { setProductModal(null); loadProducts(); }}
        />
      )}
      {checkoutOpen && (
        <CheckoutModal
          cart={cart}
          onClose={() => setCheckoutOpen(false)}
          onConfirm={handleCheckout}
        />
      )}
    </div>
  );
};

export default POSPage;
